// src/components/backoffice/articles/ArticleEditorPage.tsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDEditor, { commands, ICommand } from "@uiw/react-md-editor";
import ReactMarkdown from "react-markdown";
import { ARTICLE_REMARK_PLUGINS } from "../../../utils/articleMarkdown";
import { resizeImageForUpload, formatFileSize } from "../../../utils/imageResize";
import {
  articlesService,
  ArticleCategory,
} from "../../../services/backoffice/articlesService";

const EDITOR_HEIGHT = 620;

const withTitle = (command: ICommand, title: string): ICommand => ({
  ...command,
  buttonProps: { ...command.buttonProps, title, "aria-label": title },
});

// סרגל מצומצם בעברית. הכפתורים כאן הם רק אלה שהתוצאה שלהם באמת מרונדרת
// בעמוד המאמר. רשימת משימות וקו חוצה עדיין מושמטים, וכך גם קוד והערות —
// אין להם שימוש במאמרי תוכן, והם המקור לבלוקי הקוד המקריים שנמצאו בפרודקשן.
const TOOLBAR: ICommand[] = [
  withTitle(commands.bold, "מודגש"),
  withTitle(commands.italic, "נטוי"),
  commands.divider,
  // רק h2/h3: הכותרת הראשית של הדף היא שם המאמר, ואין צורך ב-h1 נוסף בגוף
  commands.group(
    [
      withTitle(commands.title2, "כותרת ראשית בגוף המאמר"),
      withTitle(commands.title3, "כותרת משנה"),
    ],
    {
      name: "title",
      groupName: "title",
      buttonProps: { title: "כותרות", "aria-label": "כותרות" },
    }
  ),
  commands.divider,
  withTitle(commands.link, "קישור"),
  withTitle(commands.quote, "ציטוט"),
  withTitle(commands.hr, "קו הפרדה"),
  commands.divider,
  withTitle(commands.unorderedListCommand, "רשימת תבליטים"),
  withTitle(commands.orderedListCommand, "רשימה ממוספרת"),
  withTitle(commands.table, "טבלה"),
];

const EXTRA_TOOLBAR: ICommand[] = [withTitle(commands.fullscreen, "מסך מלא")];

// ── שמירת טיוטה מקומית ─────────────────────────────────────────
// הטופס נשמר ל-localStorage תוך כדי כתיבה, כדי שסגירת לשונית, לחיצה על
// "ביטול" או פקיעת סשן (שמפנה לדף התחברות) לא ימחקו מאמר שלם.
// הטיוטה לא מוחלת אוטומטית — היא מוצעת לשחזור, כדי לא לדרוס גרסה
// עדכנית יותר מהשרת.
const DRAFT_KEY_PREFIX = "cartax_article_draft_";
const AUTOSAVE_DELAY_MS = 1500;

type FormSnapshot = {
  title: string;
  content: string;
  categoryId: string;
  excerpt: string;
  metaDescription: string;
  coverImageUrl: string;
  readTime: string;
  isPublished: boolean;
};

type StoredDraft = FormSnapshot & { savedAt: number };

const EMPTY_SNAPSHOT: FormSnapshot = {
  title: "",
  content: "",
  categoryId: "",
  excerpt: "",
  metaDescription: "",
  coverImageUrl: "",
  readTime: "5",
  isPublished: false,
};

const sameSnapshot = (a: FormSnapshot, b: FormSnapshot): boolean =>
  a.title === b.title &&
  a.content === b.content &&
  a.categoryId === b.categoryId &&
  a.excerpt === b.excerpt &&
  a.metaDescription === b.metaDescription &&
  a.coverImageUrl === b.coverImageUrl &&
  a.readTime === b.readTime &&
  a.isPublished === b.isPublished;

// ── עזרי כתיבה ─────────────────────────────────────────────────
const WORDS_PER_MINUTE = 200;

// מנקה סימוני Markdown כדי לספור מילים ולהפיק טקסט לתקציר
const stripMarkdown = (md: string): string =>
  md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s{0,3}[-*+]\s+/gm, "")
    .replace(/^\s{0,3}\d+\.\s+/gm, "")
    .replace(/[*_`~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const countWords = (md: string): number => {
  const text = stripMarkdown(md);
  return text ? text.split(" ").length : 0;
};

const estimateReadTime = (md: string): number =>
  Math.max(1, Math.round(countWords(md) / WORDS_PER_MINUTE));

// הפסקה הראשונה שאינה כותרת, ציטוט, רשימה או קו הפרדה
const firstParagraph = (md: string): string => {
  const blocks = md.split(/\n\s*\n/);
  for (const block of blocks) {
    const line = block.trim();
    if (!line) continue;
    if (/^#{1,6}\s/.test(line)) continue;
    if (/^>/.test(line)) continue;
    if (/^([-*+]|\d+\.)\s/.test(line)) continue;
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) continue;
    if (/^\|/.test(line)) continue;
    const clean = stripMarkdown(line);
    if (clean) return clean;
  }
  return "";
};

const truncateWords = (text: string, max: number): string => {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trim();
};

const formatDraftTime = (ts: number): string =>
  new Date(ts).toLocaleString("he-IL", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const ArticleEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [readTime, setReadTime] = useState("5");
  const [isPublished, setIsPublished] = useState(false);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPreview, setShowPreview] = useState(true);

  const [pendingDraft, setPendingDraft] = useState<StoredDraft | null>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<"cover" | "body" | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const bodyImageInputRef = useRef<HTMLInputElement>(null);

  const draftKey = `${DRAFT_KEY_PREFIX}${id ?? "new"}`;
  const baselineRef = useRef<FormSnapshot>(EMPTY_SNAPSHOT);

  const current: FormSnapshot = useMemo(
    () => ({ title, content, categoryId, excerpt, metaDescription, coverImageUrl, readTime, isPublished }),
    [title, content, categoryId, excerpt, metaDescription, coverImageUrl, readTime, isPublished]
  );

  const isDirty = !loadingData && !sameSnapshot(current, baselineRef.current);

  const wordCount = useMemo(() => countWords(content), [content]);
  const suggestedReadTime = useMemo(() => estimateReadTime(content), [content]);
  const suggestedSummary = useMemo(() => firstParagraph(content), [content]);

  // בדיקות מייעצות בלבד — לא חוסמות שמירה
  const seoChecks = useMemo(() => {
    const titleLen = title.trim().length;
    const metaLen = metaDescription.trim().length;
    const hasSubheading = /^\s{0,3}##\s/m.test(content);
    const hasBodyH1 = /^\s{0,3}#\s/m.test(content);
    // טבלה ללא שורת מפריד לא נבנית ומוצגת כטקסט עם קווים אנכיים
    const tableRows = content.match(/^\s{0,3}\|.*\|\s*$/gm) || [];
    const hasSeparator = /^\s{0,3}\|[\s:|-]+\|\s*$/m.test(content);
    const hasBrokenTable = tableRows.length > 0 && !hasSeparator;
    // טקסט שנכתב בשורות בודדות במקום פסקאות — נראה באתר כגוש אחד
    const singleBreaks = (content.match(/[^\n]\n(?!\n)/g) || []).length;
    const blankLines = (content.match(/\n\s*\n/g) || []).length;
    const looksLikeOneBlock = singleBreaks > 5 && blankLines < 2;

    return [
      {
        ok: titleLen >= 30 && titleLen <= 70,
        label: `אורך כותרת ${titleLen} תווים`,
        hint: "מומלץ 30–70 תווים",
      },
      {
        ok: metaLen >= 70 && metaLen <= 160,
        label: `Meta description ${metaLen} תווים`,
        hint: "מומלץ 70–160 תווים",
      },
      { ok: !!excerpt.trim(), label: "תקציר", hint: "מוצג בכרטיס ברשימת המאמרים" },
      { ok: !!categoryId, label: "קטגוריה", hint: "משמשת לניווט ולסינון" },
      {
        ok: hasSubheading,
        label: "כותרות משנה בגוף המאמר",
        hint: "לפחות כותרת ## אחת מפרקת את הטקסט",
      },
      {
        ok: !hasBodyH1,
        label: "אין כותרת ראשית כפולה",
        hint: "שורה שמתחילה ב-# מייצרת h1 נוסף על שם המאמר",
      },
      {
        ok: !hasBrokenTable,
        label: "טבלאות תקינות",
        hint: "לטבלה דרושה שורת מפריד (|---|---|) מתחת לשורת הכותרות",
      },
      {
        ok: !looksLikeOneBlock,
        label: "פסקאות מופרדות כראוי",
        hint: "שורה ריקה בין פסקאות — אחרת הטקסט מוצג כגוש אחד",
      },
    ];
  }, [title, metaDescription, excerpt, categoryId, content]);

  const failedChecks = seoChecks.filter((c) => !c.ok).length;

  const applySnapshot = useCallback((s: FormSnapshot) => {
    setTitle(s.title);
    setContent(s.content);
    setCategoryId(s.categoryId);
    setExcerpt(s.excerpt);
    setMetaDescription(s.metaDescription);
    setCoverImageUrl(s.coverImageUrl);
    setReadTime(s.readTime);
    setIsPublished(s.isPublished);
  }, []);

  const discardDraft = useCallback(() => {
    try { localStorage.removeItem(draftKey); } catch { /* אחסון חסום */ }
    setPendingDraft(null);
    setDraftSavedAt(null);
  }, [draftKey]);

  // מציע טיוטה מקומית רק אם היא באמת שונה ממה שהתקבל מהשרת
  const offerStoredDraft = useCallback((baseline: FormSnapshot) => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw) as StoredDraft;
      if (sameSnapshot(draft, baseline)) {
        localStorage.removeItem(draftKey);
        return;
      }
      setPendingDraft(draft);
    } catch {
      try { localStorage.removeItem(draftKey); } catch { /* אחסון חסום */ }
    }
  }, [draftKey]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await articlesService.getCategories();
      setCategories(data);
    } catch (err: any) {
      console.error("Failed to load categories:", err);
    }
  }, []);

  const loadArticle = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingData(true);
      const data = await articlesService.getArticles({ id });
      const article = data.articles?.[0];
      if (article) {
        const snapshot: FormSnapshot = {
          title: article.title,
          content: article.content || "",
          categoryId: article.category_id ? String(article.category_id) : "",
          excerpt: article.excerpt || "",
          metaDescription: article.meta_description || "",
          coverImageUrl: article.cover_image_url || "",
          readTime: String(article.read_time_minutes || 5),
          isPublished: !!article.is_published,
        };
        applySnapshot(snapshot);
        baselineRef.current = snapshot;
        offerStoredDraft(snapshot);
      }
    } catch (err: any) {
      setError(`שגיאה בטעינת המאמר: ${err.message}`);
    } finally {
      setLoadingData(false);
    }
  }, [id, applySnapshot, offerStoredDraft]);

  useEffect(() => {
    loadCategories();
    if (isEdit) {
      loadArticle();
    } else {
      baselineRef.current = EMPTY_SNAPSHOT;
      offerStoredDraft(EMPTY_SNAPSHOT);
    }
  }, [loadCategories, loadArticle, isEdit, offerStoredDraft]);

  // שמירה אוטומטית מקומית
  useEffect(() => {
    if (loadingData) return;
    if (!isDirty) {
      try { localStorage.removeItem(draftKey); } catch { /* אחסון חסום */ }
      setDraftSavedAt(null);
      return;
    }
    const t = setTimeout(() => {
      const savedAt = Date.now();
      try {
        localStorage.setItem(draftKey, JSON.stringify({ ...current, savedAt }));
        setDraftSavedAt(savedAt);
      } catch {
        // מצב פרטי או אחסון מלא — ממשיכים בלי שמירה מקומית
      }
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [current, isDirty, loadingData, draftKey]);

  // אזהרה בסגירת לשונית / רענון
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(""), 4000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("כותרת היא שדה חובה");
      return;
    }
    if (!content.trim()) {
      setError("תוכן הוא שדה חובה");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        title,
        content,
        category_id: categoryId || "",
        excerpt,
        meta_description: metaDescription,
        cover_image_url: coverImageUrl,
        read_time_minutes: readTime,
        is_published: isPublished ? "1" : "0",
        ...(isEdit ? { id } : {}),
      };

      if (isEdit) {
        await articlesService.updateArticle(payload);
        setSuccessMessage("המאמר עודכן בהצלחה");
      } else {
        await articlesService.createArticle(payload);
        setSuccessMessage("המאמר נוצר בהצלחה");
        setTimeout(() => navigate("/backoffice/articles"), 1500);
      }

      // נשמר בשרת — הטיוטה המקומית כבר לא נחוצה
      baselineRef.current = current;
      discardDraft();
    } catch (err: any) {
      // הטיוטה המקומית נשארת בכוונה, כדי שהטקסט ישרוד כישלון שמירה
      setError(`שגיאה בשמירה: ${err.message}. הטקסט נשמר מקומית בדפדפן.`);
    } finally {
      setLoading(false);
    }
  };

  // מקטין בדפדפן ואז מעלה. מחזיר את הנתיב היחסי, או null אם נכשל.
  const uploadImage = async (
    file: File,
    label: "cover" | "body"
  ): Promise<string | null> => {
    try {
      setUploadingImage(label);
      setError("");
      const resized = await resizeImageForUpload(file);
      const url = await articlesService.uploadArticleImage(resized.file);
      if (resized.finalSize < resized.originalSize) {
        setSuccessMessage(
          `התמונה הועלתה (${formatFileSize(resized.originalSize)} → ${formatFileSize(resized.finalSize)})`
        );
      } else {
        setSuccessMessage("התמונה הועלתה");
      }
      return url;
    } catch (err: any) {
      setError(`שגיאה בהעלאת התמונה: ${err.message}`);
      return null;
    } finally {
      setUploadingImage(null);
    }
  };

  const handleCoverSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // מאפשר לבחור שוב את אותו קובץ
    if (!file) return;
    const url = await uploadImage(file, "cover");
    if (url) setCoverImageUrl(url);
  };

  const handleBodyImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const alt = window.prompt(
      "תיאור קצר של התמונה (חשוב לנגישות ולגוגל, ומוצג אם התמונה לא נטענת):",
      ""
    );
    if (alt === null) return; // המשתמש ביטל
    const url = await uploadImage(file, "body");
    if (!url) return;

    // מוסיפים שורות ריקות מסביב כדי שהתמונה תעמוד כבלוק ולא בתוך פסקה
    const snippet = `\n\n![${alt.trim()}](${url})\n\n`;
    setContent((prev) => (prev ? `${prev.replace(/\s+$/, "")}${snippet}` : snippet.trimStart()));
  };

  const leaveToList = () => {
    setShowLeaveConfirm(false);
    navigate("/backoffice/articles");
  };

  const requestLeave = () => {
    if (isDirty) {
      setShowLeaveConfirm(true);
    } else {
      leaveToList();
    }
  };

  if (loadingData) {
    return (
      <div className="p-6 text-center text-gray-500" dir="rtl">
        טוען מאמר...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-800">
            {isEdit ? "עריכת מאמר" : "מאמר חדש"}
          </h1>
          {isDirty && (
            <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
              שינויים שלא נשמרו
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {isEdit && (
            <a
              href={`/backoffice/articles/preview/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              title="נפתח בלשונית חדשה ומציג את הגרסה השמורה בשרת"
              className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
            >
              תצוגה מקדימה מלאה
            </a>
          )}
          <button
            onClick={requestLeave}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← חזרה לרשימה
          </button>
        </div>
      </div>

      {pendingDraft && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg mb-4 flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm">
            נמצאה טיוטה מקומית שלא נשמרה, מ־{formatDraftTime(pendingDraft.savedAt)}.
            {" "}רוצה לשחזר אותה?
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => {
                applySnapshot(pendingDraft);
                setPendingDraft(null);
              }}
              className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
            >
              שחזר טיוטה
            </button>
            <button
              onClick={discardDraft}
              className="px-3 py-1 text-sm border border-amber-300 text-amber-800 rounded hover:bg-amber-100"
            >
              מחק טיוטה
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-6 space-y-5">
        {/* כותרת */}
        <div>
          <label
            htmlFor="article-title"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            כותרת <span className="text-red-500">*</span>
            <span className="sr-only">(שדה חובה)</span>
          </label>
          <input
            id="article-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-required="true"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="כותרת המאמר"
          />
        </div>

        {/* קטגוריה + זמן קריאה */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="article-category"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              קטגוריה
            </label>
            <select
              id="article-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">ללא קטגוריה</option>
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="article-read-time"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              זמן קריאה (דקות)
            </label>
            <input
              id="article-read-time"
              type="number"
              min="1"
              value={readTime}
              onChange={(e) => setReadTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {wordCount > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                {wordCount.toLocaleString("he-IL")} מילים ≈ {suggestedReadTime} דקות
                {String(suggestedReadTime) !== readTime && (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={() => setReadTime(String(suggestedReadTime))}
                      className="text-blue-600 hover:underline"
                    >
                      השתמש
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* תמונת נושא */}
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">
            תמונת נושא
          </span>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleCoverSelected}
            className="hidden"
          />
          {coverImageUrl ? (
            <div className="flex items-start gap-4 flex-wrap">
              <img
                src={coverImageUrl}
                alt="תמונת הנושא של המאמר"
                className="w-48 h-28 object-cover rounded-lg border border-gray-200"
              />
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingImage === "cover"}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {uploadingImage === "cover" ? "מעלה..." : "החלף תמונה"}
                </button>
                <button
                  type="button"
                  onClick={() => setCoverImageUrl("")}
                  className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  הסר תמונה
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingImage === "cover"}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {uploadingImage === "cover" ? "מעלה..." : "בחר תמונה"}
              </button>
              <span className="text-xs text-gray-500">
                מוצגת בראש המאמר, בכרטיס ברשימה, ובשיתוף בוואטסאפ ובפייסבוק
              </span>
            </div>
          )}
        </div>

        {/* תוכן */}
        <div>
          <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
            <label
              htmlFor="article-content"
              className="block text-sm font-medium text-gray-700"
            >
              תוכן <span className="text-red-500">*</span>
              <span className="sr-only">(שדה חובה)</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                ref={bodyImageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleBodyImageSelected}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => bodyImageInputRef.current?.click()}
                disabled={uploadingImage === "body"}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
              >
                {uploadingImage === "body" ? "מעלה..." : "הוסף תמונה לתוכן"}
              </button>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                aria-pressed={showPreview}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                {showPreview ? "הסתר תצוגה מקדימה" : "הצג תצוגה מקדימה"}
              </button>
            </div>
          </div>

          <div
            className={
              showPreview ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : ""
            }
          >
            <div data-color-mode="light">
              <MDEditor
                value={content}
                onChange={(val) => setContent(val || "")}
                height={EDITOR_HEIGHT}
                preview="edit"
                direction="rtl"
                commands={TOOLBAR}
                extraCommands={EXTRA_TOOLBAR}
                textareaProps={{
                  id: "article-content",
                  "aria-required": true,
                  placeholder:
                    "כתוב כאן את המאמר. השאר שורה ריקה בין פסקאות.",
                }}
              />
            </div>

            {showPreview && (
              <div
                className="border border-gray-200 rounded-lg overflow-hidden flex flex-col"
                style={{ height: EDITOR_HEIGHT }}
              >
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
                  כך זה ייראה באתר
                </div>
                <div className="flex-1 overflow-y-auto bg-white p-5">
                  {content.trim() ? (
                    // אותו רכיב, אותם תוספים ואותו CSS כמו ArticlePage —
                    // כדי שהתצוגה המקדימה לא תוכל להראות משהו אחר מהאתר.
                    <div className="article-content">
                      <ReactMarkdown remarkPlugins={ARTICLE_REMARK_PLUGINS}>
                        {content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">
                      התצוגה המקדימה תופיע כאן ברגע שתתחיל לכתוב.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            התצוגה המקדימה משתמשת באותו מנוע רינדור ובאותו עיצוב של עמוד המאמר
            באתר. שורה ריקה בין פסקאות פותחת פסקה חדשה.
          </p>
        </div>

        {/* תקציר */}
        <div>
          <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
            <label
              htmlFor="article-excerpt"
              className="block text-sm font-medium text-gray-700"
            >
              תקציר
            </label>
            {suggestedSummary && (
              <button
                type="button"
                onClick={() => setExcerpt(truncateWords(suggestedSummary, 200))}
                className="text-xs text-blue-600 hover:underline"
              >
                מלא מהפסקה הראשונה
              </button>
            )}
          </div>
          <textarea
            id="article-excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="תקציר קצר לתצוגה ברשימה"
          />
          <p className="text-xs text-gray-400 mt-1">{excerpt.length} תווים</p>
        </div>

        {/* Meta description */}
        <div>
          <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
            <label
              htmlFor="article-meta"
              className="block text-sm font-medium text-gray-700"
            >
              Meta Description (SEO)
            </label>
            {suggestedSummary && (
              <button
                type="button"
                onClick={() =>
                  setMetaDescription(truncateWords(suggestedSummary, 158))
                }
                className="text-xs text-blue-600 hover:underline"
              >
                מלא מהפסקה הראשונה
              </button>
            )}
          </div>
          <input
            id="article-meta"
            type="text"
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            maxLength={160}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="עד 160 תווים"
          />
          <p
            className={`text-xs mt-1 ${
              metaDescription.length >= 70 && metaDescription.length <= 160
                ? "text-green-600"
                : "text-gray-400"
            }`}
          >
            {metaDescription.length}/160
            {metaDescription.length > 0 && metaDescription.length < 70
              ? " — קצר מהמומלץ (70+)"
              : ""}
          </p>
        </div>

        {/* בדיקות מייעצות */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-700">
              בדיקות לפני פרסום
            </span>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                failedChecks === 0
                  ? "bg-green-100 text-green-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {failedChecks === 0
                ? "הכל תקין"
                : `${failedChecks} להשלמה`}
            </span>
          </div>
          <ul className="divide-y divide-gray-100">
            {seoChecks.map((check) => (
              <li
                key={check.label}
                className="px-4 py-2 flex items-start gap-3 text-sm"
              >
                <span
                  aria-hidden="true"
                  className={check.ok ? "text-green-600" : "text-amber-500"}
                >
                  {check.ok ? "✓" : "!"}
                </span>
                <span className="sr-only">{check.ok ? "תקין" : "לטיפול"}</span>
                <span className={check.ok ? "text-gray-500" : "text-gray-800"}>
                  {check.label}
                  {!check.ok && (
                    <span className="text-gray-500"> — {check.hint}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <p className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            הבדיקות מייעצות בלבד ואינן חוסמות שמירה או פרסום.
          </p>
        </div>

        {/* פרסום + כפתורי שמירה */}
        <div className="flex items-center justify-between pt-4 border-t">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600"
            />
            <span className="text-sm font-medium text-gray-700">פרסם מאמר</span>
          </label>

          <div className="flex items-center gap-3">
            {draftSavedAt && (
              <span className="text-xs text-gray-400">
                נשמר מקומית {formatDraftTime(draftSavedAt)}
              </span>
            )}
            <button
              onClick={requestLeave}
              className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "שומר..." : isEdit ? "שמור שינויים" : "צור מאמר"}
            </button>
          </div>
        </div>
      </div>

      {/* אישור יציאה עם שינויים שלא נשמרו */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" dir="rtl">
            <h2 className="text-lg font-bold text-gray-800 mb-3">
              יש שינויים שלא נשמרו
            </h2>
            <p className="text-gray-600 mb-6">
              הטיוטה נשמרה מקומית בדפדפן ותוצע לך לשחזור בכניסה הבאה למאמר הזה,
              אבל היא לא נשמרה בשרת.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                המשך לערוך
              </button>
              <button
                onClick={leaveToList}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                צא בלי לשמור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleEditorPage;
